import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trophy, Search, MapPin, Filter, Star, ArrowRight, Building2, SlidersHorizontal, Sparkles } from "lucide-react";
import { STATES, getStateName, getCityUrl } from "../constants";
import { getCourses, getCoursesSync, getLocalCourses } from "../api";
import { ListingCard } from "../components/CourseCard";
import SEO from "../components/SEO";
import { buildCollectionSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const PAGE_SIZE = 24;

const CoursesDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const initialState = searchParams.get("state") || "all";

  const [courses, setCourses] = useState(() => getCoursesSync());
  const [search, setSearch] = useState(initialSearch);
  const [selectedState, setSelectedState] = useState(initialState);
  const [minRating, setMinRating] = useState("all");
  const [sortBy, setSortBy] = useState("rating"); // "rating" | "reviews" | "name"
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    getCourses()
      .then((data) => {
        if (Array.isArray(data)) setCourses(data);
      })
      .catch(() => {});
  }, []);

  // Update query params when filters change
  const handleStateChange = (st) => {
    setSelectedState(st);
    setVisibleCount(PAGE_SIZE);
    const params = new URLSearchParams(searchParams);
    if (st && st !== "all") params.set("state", st);
    else params.delete("state");
    setSearchParams(params, { replace: true });
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    setVisibleCount(PAGE_SIZE);
    const params = new URLSearchParams(searchParams);
    if (val.trim()) params.set("q", val.trim());
    else params.delete("q");
    setSearchParams(params, { replace: true });
  };

  // Unique cities count across all courses
  const totalCitiesCount = useMemo(() => {
    const citiesSet = new Set();
    courses.forEach((c) => {
      if (c.city && c.state) {
        citiesSet.add(`${c.city.toLowerCase()}:${c.state.toLowerCase()}`);
      }
    });
    return citiesSet.size;
  }, [courses]);

  // States with course counts
  const stateCounts = useMemo(() => {
    const counts = {};
    courses.forEach((c) => {
      const code = (c.state || "").toLowerCase();
      counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [courses]);

  // Filtered courses
  const filtered = useMemo(() => {
    let list = [...courses];

    if (selectedState && selectedState !== "all") {
      list = list.filter((c) => (c.state || "").toLowerCase() === selectedState.toLowerCase());
    }

    if (minRating !== "all") {
      const min = parseFloat(minRating);
      list = list.filter((c) => (c.rating || 0) >= min);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.city || "").toLowerCase().includes(q) ||
          (c.state || "").toLowerCase().includes(q) ||
          (c.address || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q)
      );
    }

    if (sortBy === "rating") {
      list.sort((a, b) => (b.rating || 0) - (a.rating || 0) || (b.reviewCount || 0) - (a.reviewCount || 0));
    } else if (sortBy === "reviews") {
      list.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
    } else if (sortBy === "name") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return list;
  }, [courses, selectedState, minRating, search, sortBy]);

  const pageTitle = selectedState !== "all"
    ? `Mini Golf Courses in ${getStateName(selectedState)} | Complete Directory`
    : `All Mini Golf Courses in USA | Complete Directory`;

  const pageSubtitle = selectedState !== "all"
    ? `Explore all verified mini golf putt putt courses in ${getStateName(selectedState)}. Find top rated indoor & outdoor courses near you.`
    : `Explore all ${courses.length}+ verified mini golf and putt-putt destinations across ${totalCitiesCount}+ cities in all 50 states.`;

  const directorySchemas = [
    buildCollectionSchema(pageTitle, pageSubtitle, filtered, "https://minigolfalabama.com/courses"),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "All Courses Directory", url: "/courses" },
    ]),
  ];

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]" data-testid="all-courses-directory-page">
      <SEO
        title={`${pageTitle} (${courses.length}+ Venues)`}
        description={pageSubtitle.slice(0, 160)}
        canonicalUrl="https://minigolfalabama.com/courses"
        ogType="website"
        schema={directorySchemas}
      />

      {/* ================= HERO HEADER ================= */}
      <section className="bg-gradient-to-b from-[#111217] to-[#1a1c24] text-white border-b border-gray-800 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/15 backdrop-blur-md mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#e5243b]" />
                Directory &middot; {courses.length} Verified Courses &middot; {totalCitiesCount} Cities
              </div>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
                {selectedState !== "all" ? (
                  <>
                    Mini Golf Courses in{" "}
                    <span className="bg-gradient-to-r from-[#e5243b] via-[#ff4d6d] to-[#3b82f6] bg-clip-text text-transparent">
                      {getStateName(selectedState)}
                    </span>
                  </>
                ) : (
                  <>
                    All Mini Golf Courses{" "}
                    <span className="bg-gradient-to-r from-[#e5243b] via-[#ff4d6d] to-[#3b82f6] bg-clip-text text-transparent">
                      in America
                    </span>
                  </>
                )}
              </h1>
              <p className="mt-3 text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
                {pageSubtitle}
              </p>
            </div>

            {/* Stat Counters Badge */}
            <div className="flex gap-3 sm:gap-4 flex-wrap self-start md:self-auto">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 min-w-[120px] text-center backdrop-blur-sm">
                <div className="text-2xl sm:text-3xl font-black text-white">{courses.length}</div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wider mt-1">Courses</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 min-w-[120px] text-center backdrop-blur-sm">
                <div className="text-2xl sm:text-3xl font-black text-white">{totalCitiesCount}</div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wider mt-1">Cities</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 min-w-[120px] text-center backdrop-blur-sm">
                <div className="text-2xl sm:text-3xl font-black text-white">50</div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wider mt-1">States</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FILTER / SEARCH TOOLBAR ================= */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            {/* Search Input */}
            <div className="md:col-span-5 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by course name, city, or state..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b] focus:border-transparent transition-all"
              />
              {search && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700 font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* State Filter */}
            <div className="md:col-span-3">
              <select
                value={selectedState}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b] transition-all cursor-pointer"
              >
                <option value="all">All States ({courses.length} courses)</option>
                {STATES.map((s) => {
                  const count = stateCounts[s.code] || 0;
                  return (
                    <option key={s.code} value={s.code}>
                      {s.name} ({count})
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Minimum Rating */}
            <div className="md:col-span-2">
              <select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                className="w-full py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b] transition-all cursor-pointer"
              >
                <option value="all">All Ratings</option>
                <option value="4.8">4.8+ Stars</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="md:col-span-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b] transition-all cursor-pointer"
              >
                <option value="rating">Top Rated First</option>
                <option value="reviews">Most Reviews</option>
                <option value="name">Alphabetical (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Active Results Summary */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <div>
              Showing <span className="font-bold text-gray-900">{Math.min(visibleCount, filtered.length)}</span> of{" "}
              <span className="font-bold text-gray-900">{filtered.length}</span> courses
              {selectedState !== "all" && ` in ${getStateName(selectedState)}`}
              {search && ` matching "${search}"`}
            </div>
            {(selectedState !== "all" || search || minRating !== "all") && (
              <button
                onClick={() => {
                  setSelectedState("all");
                  setSearch("");
                  setMinRating("all");
                  setSearchParams({}, { replace: true });
                }}
                className="text-[#e5243b] hover:underline font-semibold"
              >
                Reset All Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ================= COURSES GRID ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center max-w-lg mx-auto shadow-xs">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Courses Found</h3>
            <p className="text-gray-500 text-sm mb-6">
              No mini golf courses matched your current filter criteria. Try clearing your search or selecting a different state.
            </p>
            <button
              onClick={() => {
                setSelectedState("all");
                setSearch("");
                setMinRating("all");
              }}
              className="px-6 py-2.5 rounded-xl bg-[#e5243b] text-white text-sm font-bold shadow-md hover:bg-[#cf1c31] transition-all"
            >
              Show All Courses
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="courses-directory-grid">
              {filtered.slice(0, visibleCount).map((course) => (
                <ListingCard key={course.id || `${course.name}-${course.city}`} course={course} />
              ))}
            </div>

            {/* Pagination Controls */}
            {visibleCount < filtered.length && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
                <button
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="px-8 py-3.5 bg-[#e5243b] hover:bg-[#cf1c31] text-white font-bold text-sm rounded-xl shadow-md active:scale-[0.98] transition-all"
                >
                  Load More Courses ({filtered.length - visibleCount} remaining)
                </button>
                <button
                  onClick={() => setVisibleCount(filtered.length)}
                  className="px-6 py-3.5 bg-white border border-gray-300 hover:border-gray-400 text-gray-700 font-bold text-sm rounded-xl transition-all"
                >
                  Show All {filtered.length} Courses
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ================= BROWSE BY STATE DIRECTORY ================= */}
      <section className="bg-white border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#e5243b]" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                Quick Filter by State
              </h2>
              <p className="text-sm text-gray-500">Jump directly to courses in any state</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {STATES.map((s) => {
              const count = stateCounts[s.code] || 0;
              const isSelected = selectedState.toLowerCase() === s.code.toLowerCase();
              return (
                <button
                  key={s.code}
                  onClick={() => {
                    handleStateChange(s.code);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={`text-left px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-between transition-all ${
                    isSelected
                      ? "bg-[#e5243b] text-white shadow-xs"
                      : "bg-[#f8fafc] border border-gray-200 text-gray-700 hover:border-red-300 hover:text-[#e5243b]"
                  }`}
                >
                  <span className="truncate">{s.name}</span>
                  <span
                    className={`ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                      isSelected ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursesDirectory;
